import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectSelectedDatabase } from '../../common/databaseSlice';
import { useGetVisitByImagingQuery, useGetVisitByInstrumentQuery, } from '../../../api/dashboardApiSlice';
import BarChart from './BarChart';
import { Virtuoso } from 'react-virtuoso';
import LoadingDashlet from "../../../components/loaders/LoadingDashlet";
import { useOutletContext } from "react-router-dom";
import { MainDomainEnum } from "../dashboardTypes";

type BarPoint = { label: string; value: number; description?: string };

function sessionComparator(a: string, b: string): number {
    const parse = (s: string) => {

        let match = /^ses-(\d{2})([A-Z])$/i.exec(s);
        if (match) {
            const visitNumber = parseInt(match[1], 10);
            const phaseChar = match[2].toUpperCase();
            const phaseRank = (phaseCode: string) =>
                phaseCode === 'S' ? 0 : phaseCode === 'A' ? 1 : phaseCode === 'M' ? 2 : 3;
            return { group: 0, num: visitNumber, sub: phaseRank(phaseChar) };
        }
        match = /^ses-([CS])(\d{2})$/i.exec(s);
        if (match) {
            const typeCode = match[1].toUpperCase();
            const visitNumber = parseInt(match[2], 10);
            const typeRank = typeCode === 'C' ? 1 : 2;
            return { group: 1, typeRank, num: visitNumber };
        }
        return { group: 2 };
    };

    const parsedA: any = parse(a);
    const parsedB: any = parse(b);

    if (parsedA.group !== parsedB.group) return parsedA.group - parsedB.group;

    if (parsedA.group === 0) {
        if (parsedA.num !== parsedB.num) return parsedA.num - parsedB.num;
        if (parsedA.sub !== parsedB.sub) return parsedA.sub - parsedB.sub;
        return a.localeCompare(b, undefined, { numeric: true });
    }

    if (parsedA.group === 1) {
        if (parsedA.typeRank !== parsedB.typeRank) return parsedA.typeRank - parsedB.typeRank;
        if (parsedA.num !== parsedB.num) return parsedA.num - parsedB.num;
        return a.localeCompare(b, undefined, { numeric: true });
    }

    return a.localeCompare(b, undefined, { numeric: true });
}

export const ByVisits = () => {
    const database = useSelector(selectSelectedDatabase);
    const { data: constructs, isFetching: isFetchingConstructs } = useGetVisitByInstrumentQuery({ database }, { skip: !database });
    const { data: imaging, isFetching: isFetchingImaging } = useGetVisitByImagingQuery({ database }, { skip: !database });

    const {
        view,
        selectedDomain,
        instrumentFilter,
        selectedSessionFilter
    } = useOutletContext<any>();

    const [showImagingDerivatives] = useState(false); // Toggle: default hidden

    const instrumentDomainRelation = useMemo(() => {
        if (isFetchingImaging || isFetchingConstructs) return {};
        const domainInstrumentMap: Record<string, string> = {};

        Object.keys(imaging || {}).forEach((imagingInstrument) => {
            domainInstrumentMap[imagingInstrument] = MainDomainEnum.IMAGING;
        });

        const domains = Object.keys(constructs || {});
        domains.forEach((domain) => {
            const instrumentKeys = Object.keys((constructs as any)[domain]);
            instrumentKeys.forEach((instrument) => {
                domainInstrumentMap[instrument] = domain;
            });
        });
        return domainInstrumentMap;
    }, [constructs, imaging, isFetchingConstructs, isFetchingImaging]);


    const isDerivativeByStudy = (key: string): boolean => {
        const databaseUpper = String(database || '').toUpperCase();
        if (databaseUpper.includes('ABCD')) return /^MR/i.test(key);      // ABCD → keys starting with "MR"
        if (databaseUpper.includes('PRE-RELEASE') || databaseUpper.includes('ONGOING-QC')) return /^img_/i.test(key);    // HBCD → keys starting with "img_"
        return false;
    };

    const getList = () => {
        let dashletData: any;
        switch (selectedDomain) {
        case MainDomainEnum.IMAGING:
            dashletData = imaging;
            break;
        case MainDomainEnum.ALL_DOMAINS:
            dashletData = imaging;
            Object.keys(constructs || {}).forEach((key) => {
                dashletData = { ...dashletData, ...(constructs as any)[key] };
            });
            break;
        default:
            dashletData = constructs?.[selectedDomain];
            break;
        }

        const items = Object.entries(dashletData || {}).map(
            ([key, obj]: [string, any]) => ({ ...obj, __key: key })
        );


        if (instrumentFilter && instrumentFilter.trim().length > 0) {
            const query = instrumentFilter.trim().toLowerCase();
            const searched = items.filter((dashlet: any) =>
                String(dashlet?.title || "").toLowerCase().includes(query) ||
                String(dashlet?.__key || "").toLowerCase().includes(query)
            );
            return searched;
        }

        // No search: hide imaging derivatives, but only in the aggregate domains. When a specific
        // domain (e.g. Tabular Imaging) is selected, its instruments are what the user asked to see;
        // the derivative patterns are loose (ABCD's /^MR/i also matches "mri_ra_prep") and would empty it.
        const isAggregateDomain = selectedDomain === MainDomainEnum.ALL_DOMAINS
            || selectedDomain === MainDomainEnum.IMAGING;
        return showImagingDerivatives || !isAggregateDomain
            ? items
            : items.filter((d: any) => !isDerivativeByStudy(String(d?.__key || '')));
    };

    // Group by session
    const sessionCharts = useMemo(() => {
        const items: any[] = getList();
        if (!items || items.length === 0) return [];
        const sessionMap = new Map<string, BarPoint[]>();
        items.forEach((instrumentObj: any, index: number) => {
            // Use the preserved key as the x-axis label (e.g., "fc_p_nsc")
            const instrumentKey = String(instrumentObj?.__key ?? index);
            const rows = Array.isArray(instrumentObj?.data) ? instrumentObj.data : [];
            rows.forEach((row: any) => {
                const session = row?.label;
                const value = Number(row?.value ?? 0);
                if (!session || value <= 0) return;
                if (selectedSessionFilter.length > 0 && !selectedSessionFilter.includes(session)) return;
                const arr = sessionMap.get(session) ?? [];
                arr.push({
                    label: instrumentKey,
                    value,
                    description: instrumentObj?.title || ''
                });
                sessionMap.set(session, arr);
            });
        });

        const orderedSessions = Array.from(sessionMap.keys()).sort(sessionComparator);

        return orderedSessions.map((session) => ({
            title: session,
            data: (sessionMap.get(session) ?? []).sort((a, b) => b.value - a.value),
        }));
    }, [getList(), selectedSessionFilter]);

    const isLoading = isFetchingImaging || isFetchingConstructs;
    if (isLoading) return <LoadingDashlet height={'calc(33% - 16px)'} />;

    return (
        <Virtuoso
            style={{ height: "100%" }}
            data={sessionCharts}
            itemContent={(index, chart) => {
                const barCount = Array.isArray(chart?.data) ? chart.data.length : 0;
                const needsScroll = barCount > 15;

                const content = (
                    <BarChart
                        key={index}
                        title={chart.title}
                        data={chart.data}
                        selectedSessionFilter={undefined}
                        ddAvailable={false}
                        imagingData={{}}
                        eegData={undefined}
                        showDescriptionOnHover
                        showAsTable={view === "table"}
                        instrumentDomainRelation={instrumentDomainRelation}
                    />
                );
                if (!needsScroll) return content;
                return (
                    <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                        <Box>{content}</Box>
                    </Box>
                );
            }}
            components={{ Footer: () => null }}
            increaseViewportBy={{ top: 0, bottom: 0 }}
        />
    );
};

export default ByVisits;
